import React, { useState, useEffect } from 'react';
import { 
  BarChart3, TrendingUp, DollarSign, Zap, Clock, Settings, 
  Download, Calendar, Eye, RefreshCw, Activity, ArrowLeft, Volume2,
  PhoneCall, Filter, ChevronDown, ChevronRight, Mic, MessageSquare
} from 'lucide-react';
import { api } from './services/api';

interface TokenUsage {
  website_id: string;
  website_name: string;
  domain: string;
  pipelines: {
    standard: { tokens: number; cost: number; calls: number };
    gemini_realtime: { tokens: number; cost: number; calls: number };
    openai_realtime: { tokens: number; cost: number; calls: number };
  };
  services: {
    google_llm: { tokens: number; cost: number; requests: number };
    openai_llm: { tokens: number; cost: number; requests: number };
    deepgram_stt: { tokens: number; cost: number; requests: number };
    cartesia_tts: { tokens: number; cost: number; requests: number };
    gemini_realtime: { tokens: number; cost: number; requests: number };
    openai_realtime: { tokens: number; cost: number; requests: number };
  };
  total_tokens: number;
  total_cost: number;
}

interface CallHistory {
  call_id: string;
  website_id: string;
  session_id: string;
  pipeline_type: string;
  call_start_time: string;
  call_end_time: string;
  call_duration_seconds: number;
  total_tokens: number;
  total_cost: number;
  status: string;
  service_details: Array<{
    service_type: string;
    tokens_used: number;
    cost_estimate: number;
    metadata: string;
  }>;
}

interface PipelineComparison {
  pipeline_type: string;
  call_count: number;
  total_tokens: number;
  total_cost: number;
  avg_tokens: number;
  avg_cost: number;
}

interface EnhancedTokenDashboardProps {
  onBack: () => void;
}

const EnhancedTokenDashboard: React.FC<EnhancedTokenDashboardProps> = ({ onBack }) => {
  const [usageData, setUsageData] = useState<TokenUsage[]>([]);
  const [callHistory, setCallHistory] = useState<CallHistory[]>([]);
  const [pipelineComparison, setPipelineComparison] = useState<PipelineComparison[]>([]);
  const [websites, setWebsites] = useState<any[]>([]);
  const [selectedWebsite, setSelectedWebsite] = useState<string>('all');
  const [selectedPipeline, setSelectedPipeline] = useState<string>('all');
  const [timeRange, setTimeRange] = useState<number>(30);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'calls' | 'pipeline'>('overview');
  const [expandedCalls, setExpandedCalls] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadAllData();
  }, [selectedWebsite, timeRange, selectedPipeline]);

  const loadAllData = async () => {
    setIsLoading(true);
    try {
      // Load websites
      const websiteList = await api.getWebsites();
      setWebsites(websiteList);
      
      // Load usage data
      await loadUsageData(websiteList);
      
      // Load call history
      await loadCallHistory();
      
      // Load pipeline comparison
      await loadPipelineComparison();
      
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadUsageData = async (websiteList: any[]) => {
    try {
      const filteredWebsites = selectedWebsite === 'all' 
        ? websiteList 
        : websiteList.filter((w: any) => w.id === selectedWebsite);
      
      const usageData = await Promise.all(filteredWebsites.map(async (website: any) => {
        // Get token usage data from API
        const tokenData = await api.getTokenUsage(website.id, timeRange);

        // tokenData is an array, get the first item
        const websiteUsage = Array.isArray(tokenData) && tokenData.length > 0 
          ? tokenData[0] 
          : null;

        return {
          website_id: website.id,
          website_name: website.name,
          domain: website.domain,
          pipelines: websiteUsage?.pipelines || {
            standard: { tokens: 0, cost: 0, calls: 0 },
            gemini_realtime: { tokens: 0, cost: 0, calls: 0 },
            openai_realtime: { tokens: 0, cost: 0, calls: 0 }
          },
          services: websiteUsage?.services || {
            google_llm: { tokens: 0, cost: 0, requests: 0 },
            openai_llm: { tokens: 0, cost: 0, requests: 0 },
            deepgram_stt: { tokens: 0, cost: 0, requests: 0 },
            cartesia_tts: { tokens: 0, cost: 0, requests: 0 },
            gemini_realtime: { tokens: 0, cost: 0, requests: 0 },
            openai_realtime: { tokens: 0, cost: 0, requests: 0 }
          },
          total_tokens: websiteUsage?.total_tokens || 0,
          total_cost: websiteUsage?.total_cost || 0
        }
        
      }));
      
      setUsageData(usageData);
    } catch (error) {
      console.error('Failed to load usage data:', error);
      setUsageData([]);
    }
  };

  const loadCallHistory = async () => {
    try {
      const websiteId = selectedWebsite === 'all' ? undefined : selectedWebsite;
      const calls = await api.getCallHistory(websiteId, timeRange) as CallHistory[];
      // Filter by pipeline if selected
      const filteredCalls = selectedPipeline === 'all' 
        ? calls 
        : calls.filter((call: CallHistory) => call.pipeline_type === selectedPipeline);
      
      setCallHistory(filteredCalls);
    } catch (error) {
      console.error('Failed to load call history:', error);
      setCallHistory([]);
    }
  };

  const loadPipelineComparison = async () => {
    try {
      const websiteId = selectedWebsite === 'all' ? undefined : selectedWebsite;
      const comparison = await api.getPipelineComparison(websiteId, timeRange);
      
      setPipelineComparison(comparison);
    } catch (error) {
      console.error('Failed to load pipeline comparison:', error);
      setPipelineComparison([]);
    }
  };

  const toggleCallExpansion = (callId: string) => {
    const newExpanded = new Set(expandedCalls);
    if (newExpanded.has(callId)) {
      newExpanded.delete(callId);
    } else {
      newExpanded.add(callId);
    }
    setExpandedCalls(newExpanded);
  };

  const handleExport = async () => {
    try {
      let csvContent = '';
      
      if (activeTab === 'calls') {
        csvContent = 'Call ID,Pipeline,Start Time,Duration (s),Total Cost,Total Tokens,Services Used\n';
        callHistory.forEach(call => {
          const services = call.service_details.map(s => s.service_type).join('; ');
          csvContent += `${call.call_id},${call.pipeline_type},${call.call_start_time},${call.call_duration_seconds?.toFixed(2) || 0},${call.total_cost.toFixed(4)},${call.total_tokens},${services}\n`;
        });
      } else {
        csvContent = 'Website,Pipeline,Service,Tokens,Cost,Requests\n';
        usageData.forEach(website => {
          Object.entries(website.services).forEach(([service, data]) => {
            if (data.requests > 0) {
              csvContent += `${website.website_name},Mixed,${service},${data.tokens},${data.cost.toFixed(4)},${data.requests}\n`;
            }
          });
        });
      }
      
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `token_usage_${activeTab}_${timeRange}days.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  const getPipelineIcon = (pipeline: string) => {
    switch (pipeline) {
      case 'standard': return <BarChart3 className="text-blue-600" size={20} />;
      case 'gemini_realtime': return <Zap className="text-purple-600" size={20} />;
      case 'openai_realtime': return <MessageSquare className="text-green-600" size={20} />;
      default: return <Activity size={20} />;
    }
  };

  const getServiceIcon = (service: string) => {
    switch (service) {
      case 'google_llm': return <Zap className="text-blue-600" size={16} />;
      case 'openai_llm': return <MessageSquare className="text-green-600" size={16} />;
      case 'deepgram_stt': return <Mic className="text-orange-600" size={16} />;
      case 'cartesia_tts': return <Volume2 className="text-purple-600" size={16} />;
      case 'gemini_realtime': return <Zap className="text-purple-600" size={16} />;
      case 'openai_realtime': return <MessageSquare className="text-green-600" size={16} />;
      default: return <Activity size={16} />;
    }
  };

  const formatCurrency = (amount: number) => `$${amount.toFixed(4)}`;
  const formatNumber = (num: number) => num.toLocaleString();
  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}m ${secs}s`;
  };
  const formatDate = (isoString: string) => {
    return new Date(isoString).toLocaleString();
  };

  // Calculate totals
  const totals = usageData.reduce((acc, website) => ({
    tokens: acc.tokens + website.total_tokens,
    cost: acc.cost + website.total_cost,
    standardCalls: acc.standardCalls + website.pipelines.standard.calls,
    geminiCalls: acc.geminiCalls + website.pipelines.gemini_realtime.calls,
    openaiCalls: acc.openaiCalls + website.pipelines.openai_realtime.calls,
  }), { tokens: 0, cost: 0, standardCalls: 0, geminiCalls: 0, openaiCalls: 0 });

  const totalCalls = totals.standardCalls + totals.geminiCalls + totals.openaiCalls;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft size={20} />
              Back
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Enhanced Token Analytics</h1>
              <p className="text-gray-600 mt-1">Multi-pipeline tracking with call-wise analysis</p>
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleExport}
              className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            >
              <Download size={16} />
              Export
            </button>
            <button
              onClick={loadAllData}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              disabled={isLoading}
            >
              <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg p-6 mb-6">
          <div className="flex gap-6 items-center flex-wrap">
            <div className="flex items-center gap-2">
              <Calendar size={18} />
              <label className="font-medium">Time Range:</label>
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(parseInt(e.target.value))}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value={1}>Last 24 hours</option>
                <option value={7}>Last 7 days</option>
                <option value={30}>Last 30 days</option>
                <option value={90}>Last 90 days</option>
              </select>
            </div>
            
            <div className="flex items-center gap-2">
              <Eye size={18} />
              <label className="font-medium">Website:</label>
              <select
                value={selectedWebsite}
                onChange={(e) => setSelectedWebsite(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="all">All Websites</option>
                {websites.map(website => (
                  <option key={website.id} value={website.id}>
                    {website.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <Filter size={18} />
              <label className="font-medium">Pipeline:</label>
              <select
                value={selectedPipeline}
                onChange={(e) => setSelectedPipeline(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="all">All Pipelines</option>
                <option value="standard">Standard</option>
                <option value="gemini_realtime">Gemini Realtime</option>
                <option value="openai_realtime">OpenAI Realtime</option>
              </select>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              <button
                onClick={() => setActiveTab('overview')}
                className={`px-6 py-3 font-medium text-sm ${
                  activeTab === 'overview'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Overview
              </button>
              <button
                onClick={() => setActiveTab('calls')}
                className={`px-6 py-3 font-medium text-sm ${
                  activeTab === 'calls'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Call History
              </button>
              <button
                onClick={() => setActiveTab('pipeline')}
                className={`px-6 py-3 font-medium text-sm ${
                  activeTab === 'pipeline'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Pipeline Comparison
              </button>
            </nav>
          </div>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <>
            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
              <div className="bg-white rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Calls</p>
                    <p className="text-2xl font-bold text-gray-900">{formatNumber(totalCalls)}</p>
                  </div>
                  <PhoneCall className="text-blue-600" size={24} />
                </div>
              </div>
              
              <div className="bg-white rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Tokens</p>
                    <p className="text-2xl font-bold text-gray-900">{formatNumber(totals.tokens)}</p>
                  </div>
                  <Zap className="text-yellow-600" size={24} />
                </div>
              </div>
              
              <div className="bg-white rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Cost</p>
                    <p className="text-2xl font-bold text-gray-900">{formatCurrency(totals.cost)}</p>
                  </div>
                  <DollarSign className="text-green-600" size={24} />
                </div>
              </div>

              <div className="bg-white rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Avg Cost/Call</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {formatCurrency(totalCalls > 0 ? totals.cost / totalCalls : 0)}
                    </p>
                  </div>
                  <TrendingUp className="text-purple-600" size={24} />
                </div>
              </div>

              <div className="bg-white rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Avg Tokens/Call</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {formatNumber(totalCalls > 0 ? Math.round(totals.tokens / totalCalls) : 0)}
                    </p>
                  </div>
                  <Activity className="text-orange-600" size={24} />
                </div>
              </div>
            </div>

            {/* Pipeline Distribution */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
              <div className="bg-white rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  {getPipelineIcon('standard')}
                  <h3 className="text-lg font-semibold">Standard Pipeline</h3>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Calls:</span>
                    <span className="font-semibold">{formatNumber(totals.standardCalls)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Cost:</span>
                    <span className="font-semibold text-blue-600">
                      {formatCurrency(usageData.reduce((sum, w) => sum + w.pipelines.standard.cost, 0))}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  {getPipelineIcon('gemini_realtime')}
                  <h3 className="text-lg font-semibold">Gemini Realtime</h3>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Calls:</span>
                    <span className="font-semibold">{formatNumber(totals.geminiCalls)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Cost:</span>
                    <span className="font-semibold text-purple-600">
                      {formatCurrency(usageData.reduce((sum, w) => sum + w.pipelines.gemini_realtime.cost, 0))}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  {getPipelineIcon('openai_realtime')}
                  <h3 className="text-lg font-semibold">OpenAI Realtime</h3>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Calls:</span>
                    <span className="font-semibold">{formatNumber(totals.openaiCalls)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Cost:</span>
                    <span className="font-semibold text-green-600">
                      {formatCurrency(usageData.reduce((sum, w) => sum + w.pipelines.openai_realtime.cost, 0))}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Service Breakdown */}
            <div className="bg-white rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-4">Service Usage</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(usageData.reduce((acc, website) => {
                  Object.entries(website.services).forEach(([service, data]) => {
                    if (!acc[service]) {
                      acc[service] = { tokens: 0, cost: 0, requests: 0 };
                    }
                    acc[service].tokens += data.tokens;
                    acc[service].cost += data.cost;
                    acc[service].requests += data.requests;
                  });
                  return acc;
                }, {} as Record<string, any>)).map(([service, data]) => (
                  data.requests > 0 && (
                    <div key={service} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        {getServiceIcon(service)}
                        <div>
                          <p className="font-medium text-sm capitalize">
                            {service.replace(/_/g, ' ').toUpperCase()}
                          </p>
                          <p className="text-xs text-gray-600">{formatNumber(data.requests)} requests</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-sm">{formatCurrency(data.cost)}</p>
                        <p className="text-xs text-gray-600">{formatNumber(data.tokens)} tokens</p>
                      </div>
                    </div>
                  )
                ))}
              </div>
            </div>
          </>
        )}

        {/* Call History Tab */}
        {activeTab === 'calls' && (
          <div className="bg-white rounded-lg shadow-sm border">
            <div className="p-6 border-b">
              <h2 className="text-xl font-semibold">Call-wise Usage History</h2>
              <p className="text-sm text-gray-600 mt-1">
                Detailed breakdown of each conversation call
              </p>
            </div>
            
            <div className="divide-y">
              {callHistory.map((call) => (
                <div key={call.call_id} className="p-6">
                  <div 
                    className="flex items-center justify-between cursor-pointer"
                    onClick={() => toggleCallExpansion(call.call_id)}
                  >
                    <div className="flex items-center gap-4 flex-1">
                      <button className="text-gray-400 hover:text-gray-600">
                        {expandedCalls.has(call.call_id) ? (
                          <ChevronDown size={20} />
                        ) : (
                          <ChevronRight size={20} />
                        )}
                      </button>
                      
                      <div className="flex items-center gap-3">
                        {getPipelineIcon(call.pipeline_type)}
                        <div>
                          <div className="font-medium text-gray-900">
                            {call.pipeline_type.replace(/_/g, ' ').toUpperCase()}
                          </div>
                          <div className="text-sm text-gray-500">
                            {formatDate(call.call_start_time)}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Clock size={14} />
                        {formatDuration(call.call_duration_seconds || 0)}
                      </div>

                      <div className={`px-2 py-1 rounded text-xs font-medium ${
                        call.status === 'completed' 
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {call.status}
                      </div>
                    </div>

                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <div className="font-semibold text-gray-900">
                          {formatCurrency(call.total_cost)}
                        </div>
                        <div className="text-sm text-gray-600">
                          {formatNumber(call.total_tokens)} tokens
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Expanded Service Details */}
                  {expandedCalls.has(call.call_id) && call.service_details && (
                    <div className="mt-4 ml-12 space-y-3">
                      <div className="text-sm font-medium text-gray-700 mb-2">Service Breakdown:</div>
                      {call.service_details.map((service, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                          <div className="flex items-center gap-3">
                            {getServiceIcon(service.service_type)}
                            <span className="text-sm capitalize">
                              {service.service_type.replace(/_/g, ' ')}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 text-sm">
                            <span className="text-gray-600">
                              {formatNumber(service.tokens_used)} tokens
                            </span>
                            <span className="font-medium">
                              {formatCurrency(service.cost_estimate)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {callHistory.length === 0 && !isLoading && (
              <div className="text-center py-12">
                <PhoneCall size={48} className="mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No calls found</h3>
                <p className="text-gray-500">Call history will appear here once conversations start.</p>
              </div>
            )}
          </div>
        )}

        {/* Pipeline Comparison Tab */}
        {activeTab === 'pipeline' && (
          <div className="bg-white rounded-lg shadow-sm border">
            <div className="p-6 border-b">
              <h2 className="text-xl font-semibold">Pipeline Performance Comparison</h2>
              <p className="text-sm text-gray-600 mt-1">
                Compare efficiency and costs across different pipelines
              </p>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pipeline</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Calls</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Tokens</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Cost</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avg Tokens/Call</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avg Cost/Call</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {pipelineComparison.map((pipeline) => (
                    <tr key={pipeline.pipeline_type} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          {getPipelineIcon(pipeline.pipeline_type)}
                          <span className="font-medium capitalize">
                            {pipeline.pipeline_type.replace(/_/g, ' ')}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 font-medium">{formatNumber(pipeline.call_count)}</td>
                      <td className="px-6 py-4">{formatNumber(pipeline.total_tokens)}</td>
                      <td className="px-6 py-4 font-semibold">{formatCurrency(pipeline.total_cost)}</td>
                      <td className="px-6 py-4 text-gray-600">{formatNumber(Math.round(pipeline.avg_tokens))}</td>
                      <td className="px-6 py-4 text-gray-600">{formatCurrency(pipeline.avg_cost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {pipelineComparison.length === 0 && !isLoading && (
                <div className="text-center py-12">
                  <BarChart3 size={48} className="mx-auto text-gray-300 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No pipeline data available</h3>
                  <p className="text-gray-500">Data will appear once calls are processed.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 flex items-center gap-3">
              <RefreshCw size={24} className="animate-spin text-blue-600" />
              <span>Loading data...</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EnhancedTokenDashboard;